import qs from 'query-string';

import { AVAILABLE_DEEP_SEARCH_TAGS } from '../constants/label';
import { ACCESS_PUBLIC } from '../constants/video';
import { ALL_SOCIAL_ENGAGEMENT_TYPES, LIKES, SHARES, VIEWS } from '../constants/videoEngagement';

import { TMVideoType, VideoType } from '@/modules/common/types/video';

import { deepClone } from '@/modules/common/helpers/object';
import { grepVideoResources } from '@/modules/common/helpers/videoCache';

import { collectVideoEntities } from './chips';
import { replaceDomain } from './url';

let wid: string | null = null;

export const setPlayerName = (playerName: string) => {
  wid = playerName;
};

const setWidQuery = (sourceUrl: string, signedUrlExp: number | undefined) => {
  const [url, stringQuery] = sourceUrl.split('?');

  const parsedParams = qs.parse(stringQuery);

  if (!signedUrlExp) {
    parsedParams.wid = wid;
  }

  return `${url}?${qs.stringify(parsedParams)}`;
};

const processUrl = (urlSource: string, signedUrlExp: number | undefined) =>
  setWidQuery(replaceDomain(urlSource), signedUrlExp);

export const getFormattedVideo = (
  video: TMVideoType,
  options: {
    signedUrlExp: number | undefined;
  },
): VideoType => {
  const $$sourceVideo = deepClone(video) as Record<string, unknown>;

  if (options.signedUrlExp) {
    grepVideoResources(video, options.signedUrlExp);
  }

  return {
    ...video,
    title: video.title ?? '',
    defaultOrder: video.defaultOrder ?? 0,
    mediaId: video.mediaid,
    mediaid: video.mediaid,
    images: (video.images ?? []).map((video$) => ({
      ...video$,
      file: processUrl(video$.file, options.signedUrlExp),
    })),
    files: (video.files ?? []).map((file$) => ({
      ...file$,
      file: processUrl(file$.file, options.signedUrlExp),
    })),
    file: video.file ?? '',
    ccFiles: (video.ccFiles ?? []).map((file$) => ({
      ...file$,
      file: processUrl(file$.file, options.signedUrlExp),
    })),
    previewUrl: video.previewUrl ? processUrl(video.previewUrl, options.signedUrlExp) : '',
    plot: video.plot ?? '',
    duration: video.duration ?? 0,
    created: video.created ?? 0,
    performanceData: {
      time: video.performanceData?.time ?? 0,
      parameters: ALL_SOCIAL_ENGAGEMENT_TYPES.map((type) => {
        const serverItem = video.performanceData?.parameters?.find((param$) => param$.type === type);

        return {
          type,
          value: serverItem?.value ?? 0,
        };
      }),
    },
    trending: video.trending ?? 0,
    ccUrl: video.ccUrl ? processUrl(video.ccUrl, options.signedUrlExp) : '',
    describe: (video.describe || []).filter((item) => item.type && AVAILABLE_DEEP_SEARCH_TAGS.includes(item.type)),
    highlight: (video.highlight ?? []).map((oneHighlight) => ({
      ...oneHighlight,
      stopWords: (oneHighlight.stopWords ?? []).slice().sort((a, b) => b.length - a.length),
      fragments: oneHighlight.fragments.slice().sort((a, b) => b.length - a.length),
    })),
    videoId: video.videoId ?? '',
    highlights: video.highlights ?? false,
    slides: video.slides ?? false,
    access: {
      users: video.access?.users ?? [],
      level: video.access?.level ?? ACCESS_PUBLIC,
    },
    keywords: video.keywords || [],
    label: video.label || [],
    iab: {
      categories: video?.iab?.categories,
    },
    videoEntities: collectVideoEntities(video),
    hlsFile: video.hlsFile?.file
      ? {
          ...video.hlsFile,
          file: processUrl(video.hlsFile.file, options.signedUrlExp),
        }
      : undefined,
    $$sourceVideo,
  };
};

export const getOriginalVideoEngagementData = (params: VideoType['performanceData']['parameters']) =>
  ALL_SOCIAL_ENGAGEMENT_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type]: params.find((item$) => item$.type === type)?.value ?? 0,
    }),
    {} as Record<(typeof ALL_SOCIAL_ENGAGEMENT_TYPES)[number], number>,
  );

const socialTypeMinValue = {
  [VIEWS]: 10,
  [LIKES]: 1,
  [SHARES]: 1,
};
export const getVideoEngagementData = (
  performanceDataParameters: VideoType['performanceData']['parameters'],
  engagement: Partial<(typeof ALL_SOCIAL_ENGAGEMENT_TYPES)[number][]>,
  showLike: boolean = false,
) =>
  ALL_SOCIAL_ENGAGEMENT_TYPES.reduce(
    (acc, type) => {
      const item = engagement.includes(type)
        ? performanceDataParameters.find((item$) => item$.type === type)
        : undefined;

      const value = item?.value ?? 0;

      if ((showLike && type === LIKES) || value >= socialTypeMinValue[type]) {
        return {
          ...acc,
          [type]: value,
        };
      }

      return acc;
    },
    {} as Record<(typeof ALL_SOCIAL_ENGAGEMENT_TYPES)[number], number>,
  );
